import { useState, useEffect, useCallback } from "react";
import { UserPlus, Trash2, Loader2, ShieldCheck, KeyRound, Smartphone, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { listUsers, createUser, deleteUser, resetUserTotp } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function UsersPanel() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", role: "consulente", password: "" });
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(() => listUsers().then(setUsers).catch(() => toast.error("Impossibile caricare gli utenti")), []);
  useEffect(() => { refresh(); }, [refresh]);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await createUser(form);
      toast.success("Utente autorizzato");
      setForm({ email: "", name: "", role: "consulente", password: "" });
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Creazione non riuscita");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (email) => {
    if (!window.confirm(`Revocare l'accesso a ${email}?`)) return;
    try {
      await deleteUser(email);
      toast.success("Accesso revocato");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eliminazione non riuscita");
    }
  };

  const resetTotp = async (email) => {
    if (!window.confirm(`Azzerare Google Authenticator per ${email}? Al prossimo accesso dovrà inquadrare un nuovo QR.`)) return;
    try {
      await resetUserTotp(email);
      toast.success("Authenticator azzerato");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Operazione non riuscita");
    }
  };

  return (
    <div data-testid="users-panel" className="grid lg:grid-cols-3 gap-6">
      <form onSubmit={add} className="rounded-xl bg-slate-900 border border-white/10 p-6 space-y-4">
        <div className="flex items-center gap-2">
          <UserPlus className="h-5 w-5 text-accent" />
          <h3 className="font-display font-semibold text-white">Autorizza consulente</h3>
        </div>
        <p className="text-xs text-slate-400">Il consulente accede con <b className="text-slate-200">nome e cognome</b> + password; al primo accesso attiva Google Authenticator inquadrando un QR.</p>
        <Input data-testid="user-email-input" type="email" required placeholder="email@esempio.it" value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-slate-950 border-white/10 text-white" />
        <Input data-testid="user-name-input" required placeholder="Nome e cognome (usato per il login)" value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-slate-950 border-white/10 text-white" />
        <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
          <SelectTrigger data-testid="user-role-select" className="bg-slate-950 border-white/10 text-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="consulente">Consulente</SelectItem>
            <SelectItem value="admin">Amministratore</SelectItem>
          </SelectContent>
        </Select>
        <Input data-testid="user-password-input" type="password" required placeholder="Password (min 8 caratteri)" value={form.password}
          minLength={8}
          onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-slate-950 border-white/10 text-white" />
        <Button data-testid="user-create-button" type="submit" disabled={busy} className="w-full gap-2 bg-accent hover:bg-accent/90 text-white">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Autorizza
        </Button>
      </form>

      <div className="lg:col-span-2 rounded-xl border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-900 text-slate-400">
            <tr>
              <th className="text-left font-medium px-4 py-3">Utente</th>
              <th className="text-left font-medium px-4 py-3">Ruolo</th>
              <th className="text-left font-medium px-4 py-3 hidden sm:table-cell">Accesso</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.email} data-testid={`user-row-${u.email}`} className="border-t border-white/5">
                <td className="px-4 py-3">
                  <div className="font-medium text-white">{u.name || "—"}</div>
                  <div className="text-xs text-slate-500">{u.email}</div>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded border ${u.role === "admin" ? "bg-amber-100 text-amber-800 border-amber-300" : "bg-slate-800 text-slate-300 border-white/10"}`}>
                    {u.role}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-slate-400 hidden sm:table-cell">
                  <span className="inline-flex items-center gap-1"><KeyRound className="h-3.5 w-3.5 text-sky-400" /> Password</span>
                  <span className={`inline-flex items-center gap-1 ml-3 ${u.totp_enabled ? "" : "text-amber-400"}`}>
                    {u.totp_enabled ? <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" /> : <Smartphone className="h-3.5 w-3.5" />}
                    {u.totp_enabled ? "Authenticator attivo" : "Authenticator da attivare"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {u.totp_enabled && (
                    <Button data-testid={`user-reset-totp-${u.email}`} variant="ghost" size="icon" onClick={() => resetTotp(u.email)}
                      title="Azzera Google Authenticator" className="text-slate-500 hover:text-amber-400">
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                  )}
                  {u.email !== user?.email && (
                    <Button data-testid={`user-delete-${u.email}`} variant="ghost" size="icon" onClick={() => remove(u.email)}
                      className="text-slate-500 hover:text-rose-400">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
